// A published workflow's keys (api/workflowPublish.ts): what the Publish
// dialog shows and checks, so the dialog stays thin. Pure:
// ./publishModel.selftest.ts pins it.
//
// The table: Name · Made · Last used · Ends · Runs, live keys first (newest
// first), then the ended ones (still with Revoke), then the revoked ones,
// which say "Revoked" where a live key has its Revoke. A new key needs a
// name of 1-40 characters that no key of this workflow has had (names are
// never reused: a revoked key's runs still name it). Ten live keys at most:
// live = neither revoked nor ended, as the server counts them. A run a key
// started is "via <name>"
// wherever the dashboard says who started a run (`started_by` is
// `key:<name>`); the run's status words are the server's.

import type { KeyLimits, KeyView, PublishView } from "@/api/workflowPublish";
import { formatDate, formatDateTime, formatRelative, toDate, type DateInput } from "@/lib/format";

import { endWord, isLive } from "../published/publishedModel";

/** Live keys a workflow may have at once (the server's 422 `too_many_keys`). */
export const MAX_LIVE_KEYS = 10;
/** The longest key name. */
export const MAX_NAME = 40;

// ─── the table ───

export interface KeyRow {
  id: string;
  name: string;
  /** "Wed 23 Sep". */
  made: string;
  /** "Wed 23 Sep, 10:00 by stanley". */
  madeTitle: string;
  /** "3 min ago", "Never". */
  lastUsed: string;
  /** "Never", "27 Oct 2027", "Ends in 3 days", "Ended" (Published's words). */
  ends: string;
  /** Within a week of its end. */
  endsWarn: boolean;
  /** The day it ends, when `ends` says it in days. */
  endsTitle?: string;
  /** "12", "12 (1 running)". */
  runs: string;
  /** "At most 20 an hour, 2 at once". */
  runsTitle: string;
  revoked: boolean;
}

/** Neither revoked nor ended (Published's isLive: the server's keys.live). */
export const isLiveKey = (k: Pick<KeyView, "revoked_at" | "ends_at">, now: Date) => isLive({ revoked_at: k.revoked_at, ends_at: k.ends_at ?? null }, now);

const time = (v: DateInput) => toDate(v)?.getTime() ?? 0;

/** "At most 20 an hour, 2 at once": a key's limits, for the Runs cell's tooltip. */
export function limitsWord(l: KeyLimits | null | undefined): string {
  if (!l) return "";
  return `At most ${l.starts_per_hour} an hour, ${l.live_runs} at once`;
}

export function keyRow(k: KeyView, now: Date): KeyRow {
  const ends = endWord({ ends_at: k.ends_at ?? null }, now);
  return {
    id: k.id,
    name: k.name,
    made: formatDate(k.created_at, {}, now),
    madeTitle: [formatDateTime(k.created_at, { relativeDay: false }, now), k.created_by ? `by ${k.created_by}` : ""].filter(Boolean).join(" "),
    lastUsed: k.last_used_at ? formatRelative(k.last_used_at, now) : "Never",
    ends: ends.text,
    endsWarn: ends.tone === "warning",
    endsTitle: ends.title,
    runs: k.live_runs > 0 && !k.revoked_at ? `${k.starts} (${k.live_runs} running)` : String(k.starts),
    runsTitle: limitsWord(k.limits),
    revoked: !!k.revoked_at,
  };
}

/** The table's rows: live keys newest first, then the ended ones (newest first), then the revoked ones, the latest revoked first. */
export function keyRows(view: Pick<PublishView, "keys">, now: Date): KeyRow[] {
  const newest = (a: KeyView, b: KeyView) => time(b.created_at) - time(a.created_at);
  const live = view.keys.filter((k) => isLiveKey(k, now)).sort(newest);
  const ended = view.keys.filter((k) => !k.revoked_at && !isLiveKey(k, now)).sort(newest);
  const revoked = view.keys.filter((k) => !!k.revoked_at).sort((a, b) => time(b.revoked_at) - time(a.revoked_at));
  return [...live, ...ended, ...revoked].map((k) => keyRow(k, now));
}

/** Why no new key can be made (ten live already: an ended key does not count), or null. */
export function newKeyBlocked(view: Pick<PublishView, "keys">, now: Date): string | null {
  return view.keys.filter((k) => isLiveKey(k, now)).length >= MAX_LIVE_KEYS ? `${MAX_LIVE_KEYS} keys at most: revoke one first.` : null;
}

// ─── a new key ───

/** What is wrong with a new key's name, or "" (an empty name is "" too: the button is off, nothing to say yet). */
export function nameProblem(name: string, keys: readonly Pick<KeyView, "name">[]): string {
  const n = name.trim();
  if (!n) return "";
  if (n.length > MAX_NAME) return `${MAX_NAME} characters at most.`;
  const lower = n.toLowerCase();
  if (keys.some((k) => k.name.trim().toLowerCase() === lower)) return "That name is taken.";
  return "";
}

/** A name that can be sent: 1-40 characters, never used here. */
export const nameReady = (name: string, keys: readonly Pick<KeyView, "name">[]) => !!name.trim() && !nameProblem(name, keys);

/**
 * The MCP config as text to copy: the server's string as it came, or (an
 * object, should the server send one) the same JSON, indented.
 */
export function snippetText(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

// ─── refusals and the revoke confirm ───

/** One sentence for a refused create or revoke; `fallback` (the server's message) for anything else. */
export function publishError(code: string | null, fallback: string): string {
  switch (code) {
    case "name_taken":
      return "That name is taken.";
    case "too_many_keys":
      return `${MAX_LIVE_KEYS} keys at most: revoke one first.`;
    case "workflows_not_enabled":
      return "Workflows are off in this workspace.";
    case "not_found":
      return "Not found: reload and try again.";
  }
  return fallback;
}

/** The revoke confirm, as on Published: "Revoke “<name>”? Callers using it stop at once." */
export { revokeConfirm } from "../published/publishedModel";

// ─── who started a run ───

const KEY_PREFIX = "key:";

/** The key's name when a key started the run (`started_by` = `key:<name>`), else null. */
export function publishedKeyName(startedBy: string | null | undefined): string | null {
  if (!startedBy || !startedBy.startsWith(KEY_PREFIX)) return null;
  return startedBy.slice(KEY_PREFIX.length).trim() || "a key";
}

/** "via Acme bot". */
export const viaKey = (name: string) => `via ${name}`;
